import React from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Code, Shield, Database, Zap, Settings, Lock } from 'lucide-react';

const Features: React.FC = () => {
  const { t } = useTranslation();
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  const features = [
    {
      icon: Code,
      title: t('features.unity'),
      description: 'Integração nativa com Unity via WhisperService.cs para máxima performance',
      color: 'primary',
    },
    {
      icon: Shield,
      title: t('features.api'),
      description: 'API REST segura com autenticação por chave e rate limiting',
      color: 'secondary',
    },
    {
      icon: Database,
      title: t('features.logs'),
      description: 'Logs detalhados e estruturados para debugging e monitoramento',
      color: 'accent',
    },
    {
      icon: Zap,
      title: t('features.categorization'),
      description: 'Categorização automática por IA com aprendizado contínuo',
      color: 'primary',
    },
    {
      icon: Settings,
      title: t('features.websocket'),
      description: 'WebSocket para dados em tempo real e comunicação bidirecional',
      color: 'secondary',
    },
    {
      icon: Lock,
      title: t('features.security'),
      description: 'Segurança robusta com roles, empresas e auditoria completa',
      color: 'accent',
    },
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
      },
    },
  };

  return (
    <section id="features" className="py-20 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          ref={ref}
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            {t('features.title')}
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            {t('features.subtitle')}
          </p>
        </motion.div>

        {/* Features Grid */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate={inView ? "visible" : "hidden"}
          className="grid md:grid-cols-2 lg:grid-cols-3 gap-8"
        >
          {features.map((feature, index) => {
            const IconComponent = feature.icon;
            const colorClasses = {
              primary: 'bg-primary-100 text-primary-600 border-primary-200',
              secondary: 'bg-secondary-100 text-secondary-600 border-secondary-200',
              accent: 'bg-accent-100 text-accent-600 border-accent-200',
            };

            return (
              <motion.div
                key={index}
                variants={itemVariants}
                className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105"
              >
                <div className={`w-16 h-16 rounded-xl ${colorClasses[feature.color as keyof typeof colorClasses]} flex items-center justify-center mb-6`}>
                  <IconComponent size={32} />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-4">
                  {feature.title}
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  {feature.description}
                </p>
              </motion.div>
            );
          })}
        </motion.div>

        {/* Technical Architecture */}
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.6 }}
          className="mt-16 bg-gradient-to-r from-primary-600 to-secondary-600 rounded-2xl p-8 text-white"
        >
          <div className="text-center mb-8">
            <h3 className="text-3xl font-bold mb-4">Arquitetura Técnica</h3>
            <p className="text-xl opacity-90">
              Tecnologia de ponta para máxima performance e escalabilidade
            </p>
          </div>

          <div className="grid md:grid-cols-4 gap-6">
            <div className="text-center">
              <div className="text-3xl font-bold mb-2">99.9%</div>
              <div className="text-sm opacity-80">Uptime</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold mb-2">&lt;100ms</div>
              <div className="text-sm opacity-80">Latência</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold mb-2">10K+</div>
              <div className="text-sm opacity-80">Concurrent Users</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold mb-2">256-bit</div>
              <div className="text-sm opacity-80">Encryption</div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Features; 